import React, { useState } from 'react'
import { Typography, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useQuote } from '../../../context'
import type { PresentationOptionV2 } from 'paintscout'
import Scrollspy from 'react-scrollspy'
import { getPresentationNavItems, getQuoteNextPresentation } from '@paintscout/util/builder'
import { usePresentationNav } from '../PresentationNavContext'
import classnames from 'classnames'
import { useClientOptions } from '@paintscout/ui-business'

const useStyles = makeStyles<Theme, PresentationNavTextProps>(
  (theme: Theme) => {
    return {
      textRoot: {
        '&::-webkit-scrollbar-track': {
          boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
          webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,.1)',
          outline: 'none'
        },
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 200px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        '& button': {
          marginTop: theme.spacing(1)
        }
      },
      buttonWrapper: {
        cursor: 'pointer',
        marginBottom: theme.spacing(0.5),
        padding: theme.spacing(0.5),
        paddingLeft: 0,
        lineHeight: 1,
        wordWrap: 'break-word',
        '&:first-child button': {
          marginTop: 0
        }
      },
      title: {
        '& h3': {
          display: 'flex',
          alignItems: 'center',
          fontSize: '1.3em',
          marginBottom: theme.spacing(1),

          paddingBottom: theme.spacing(1),
          '& svg': {
            fontSize: '1em',
            marginRight: theme.spacing(0.5)
          }
        }
      },
      button: {
        border: '1px solid transparent',
        background: 'transparent',
        color: theme.palette.grey[700],
        whiteSpace: 'normal',
        '$:click': {
          border: '1px solid transparent'
        },
        '&:hover': {
          color: '#000000',
          borderRadius: 0
        }
      },
      buttonSpan: {
        whiteSpace: 'normal',
        textAlign: 'left',
        hyphens: 'auto'
      },
      currentPage: {
        border: '1px solid transparent',
        background: 'transparent',
        color: '#000000',
        textDecoration: 'underline',
        borderRadius: 0
      },
      currentSection: {
        background: 'transparent',
        color: '#000000',
        borderRadius: 0
      },
      highlight: {},
      textDiv: {
        overflow: 'hidden',
        width: '200px'
      }
    }
  },
  { name: 'PresentationNavText' }
)

export interface PresentationNavTextProps {
  presentation?: PresentationOptionV2
}

const PresentationNavText = ({ ...props }: PresentationNavTextProps) => {
  const classes = useStyles(props)
  const { options } = useClientOptions()
  const { quote } = useQuote()
  const { currentPage, isQuoteEmpty, onPageClick, view } = usePresentationNav()
  const presentation =
    props?.presentation ??
    getQuoteNextPresentation({
      quote,
      options,
      isWorkOrder: view === 'work-order',
      isProductOrderForm: view === 'product-order-form'
    })
  const [currentScrollPage, setCurrentScrollPage] = useState<string>()

  // page tabs when multi-page, section titles when single-page — see getPresentationNavItems
  const filteredItems = getPresentationNavItems({ presentation, isQuoteEmpty })

  // The item holding the estimate gets highlighted. It deliberately does NOT open the cart — nav
  // items only navigate. Opening the estimate drawer belongs to the explicit affordances: the
  // "View Estimate" buttons in PresentationQuoteDrawer and the cartLink ButtonBlock in QuoteBlock.
  const quoteItemIndex = filteredItems.findIndex((item) => containsQuote(presentation, item.key))
  const highlight = quoteItemIndex === -1 ? null : filteredItems[quoteItemIndex].key

  return (
    <div className={classes.textRoot}>
      <Scrollspy
        componentTag={'div'}
        items={filteredItems.map((item) => item.key)}
        offset={-100}
        onUpdate={(element) => {
          setCurrentScrollPage(element?.id)
        }}
      >
        {filteredItems.map((item, index) => {
          return (
            <div
              className={classes.buttonWrapper}
              key={item.key}
              onClick={(ev) => {
                if (onPageClick) {
                  onPageClick(ev, item.key)
                  setCurrentScrollPage(item.key)
                }
              }}
            >
              <div className={classes.textDiv}>
                <Typography
                  classes={{
                    root: classnames({
                      [classes.button]: true,
                      [classes.currentPage]: isCurrentItem(item, index, currentPage),
                      [classes.highlight]: highlight === item.key,
                      [classes.buttonSpan]: true
                    })
                  }}
                  variant="overline"
                  noWrap
                >
                  {item.label}
                </Typography>
              </div>
            </div>
          )
        })}
      </Scrollspy>
    </div>
  )

  function isCurrentItem(item, index, currentKey) {
    if (currentScrollPage) {
      return item.key === currentScrollPage
    }
    return (currentKey === null && index === 0) || item.key === currentKey
  }
}

/**
 * Whether the page or section with this key contains the estimate block. The key can be either
 * level — getPresentationNavItems lists pages when multi-page and sections when single-page — so
 * both are checked rather than assuming which one the nav is showing.
 */
function containsQuote(presentation: PresentationOptionV2 | undefined, key: string): boolean {
  for (const page of presentation?.pages ?? []) {
    const sections = page.sections ?? []
    const hasQuote = (candidates: typeof sections) =>
      candidates.some((section) => (section.blocks ?? []).some((block) => block.type === 'quote'))

    if (page.key === key) {
      return hasQuote(sections)
    }

    const section = sections.find((candidate) => candidate.key === key)
    if (section) {
      return hasQuote([section])
    }
  }

  return false
}

export default PresentationNavText
